import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { PageHeader, StatusChip, displayName } from "@/components/common";

function Group({ title, items, reload, tone }) {
  const patch = async (c, data) => { try { await api.patch(`/contacts/${c.id}`, { data }); reload(); toast.success("Updated"); } catch (e) { toast.error(errMsg(e)); } };
  if (!items?.length) return null;
  return (
    <section className="mb-6">
      <h2 className={`mb-2 text-sm font-bold uppercase tracking-wide ${tone}`}>{title} ({items.length})</h2>
      <div className="space-y-2">
        {items.map((c) => (
          <div key={c.id} className="cd-card flex flex-col gap-2 py-3 sm:flex-row sm:items-center" data-testid={`followup-${c.id}`}>
            <Link to={`/contacts/${c.id}`} className="min-w-0 flex-1">
              <p className="truncate font-semibold">{displayName(c)} {c.interest_level && <span className="text-xs text-slate-500">· {c.interest_level}</span>}</p>
              <p className="truncate text-sm text-slate-500">{c.next_action || "No next action"} {c.follow_up_date && `· due ${c.follow_up_date}`}</p>
            </Link>
            <div className="flex flex-wrap items-center gap-2">
              <StatusChip status={c.status} />
              <input type="date" data-testid={`followup-date-${c.id}`} className="h-9 rounded-lg border border-slate-200 px-2 text-sm" value={c.follow_up_date || ""} onChange={(e) => patch(c, { follow_up_date: e.target.value || null })} />
              <Link to={`/compose?contact=${c.id}`} className="cd-btn-secondary h-9 px-3">Write email</Link>
              <button data-testid={`followup-done-${c.id}`} onClick={() => patch(c, { follow_up_date: null })} className="cd-btn-ghost h-9 px-3"><CheckCircle2 className="h-4 w-4" />Done</button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function FollowUps() {
  const [d, setD] = useState(null);
  const load = () => api.get("/followups").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  const empty = d && !d.overdue.length && !d.today.length && !d.upcoming.length && !d.contacted_no_date.length;
  return (
    <div>
      <PageHeader title="Follow-ups" subtitle="Internal reminders. Follow-up emails are never sent automatically." />
      {empty && <div className="cd-card flex flex-col items-center py-10 text-center"><CalendarClock className="mb-2 h-8 w-8 text-slate-300" /><p className="font-semibold">No follow-ups scheduled</p><p className="text-sm text-slate-500">Set a follow-up date on any contact.</p></div>}
      {d && <>
        <Group title="Overdue" items={d.overdue} reload={load} tone="text-rose-700" />
        <Group title="Today" items={d.today} reload={load} tone="text-amber-700" />
        <Group title="Next 7 days" items={d.upcoming} reload={load} tone="text-teal-700" />
        <Group title="Contacted — no follow-up date" items={d.contacted_no_date} reload={load} tone="text-slate-500" />
      </>}
    </div>
  );
}
